import AddIcon from "@mui/icons-material/Add";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useIdentity } from "../../../containers/IdentityContainer";
import { LocationsContainer } from "../../../containers/LocationsStateContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { usePredefinedObstacle } from "../../../hooks/usePredefinedObstacle";
import { useTenants } from "../../../hooks/useTenants";
import { getStackPolygons } from "../../../hooks/utils";
import {
  Aircraft,
  Hangar,
  Location,
  Ramp,
  Stack as StackType,
  Tenant,
  TenantType,
  Trip,
} from "../../../types";
import {
  consistentId,
  defaultPreferenceForAircraft,
  uuidv4,
} from "../../../utils";
import { checkForAircraftWarnings } from "../../../utils/aircraftWarnings";
import mixpanel from "../../../utils/mixpanel";
import { addTenantConformationText } from "../../../utils/phrasing";
import { MovableObstacleGrid } from "../../../widgets/AddMovableObstacle/MovableObstacleGrid";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog";
import { doBoundsIntersect } from "../../../widgets/Layout/widgets/CollisionOverlay";
import { useLayoutCanvasState } from "../../Ramp/RampPresenter";
import {
  SimpleBulkUpload,
  SimpleBulkUploadRef,
} from "../../Schedule/SimpleBulkUpload/SimpleBulkUpload";
import { AddSelectedTenant } from "./AddSelectedTenant";
import { AddTripTab, AddTripTabRef } from "./AddTripTab";
import { AllTripsTab } from "./AllTripsTab";
import { BulkAdd } from "./BulkAdd";
import { NewAircraftForm } from "./NewAircraftForm";
import { TenantSelector } from "./TenantSelector";

// if the bottom of the hangar/ramp doesn't appear in the current viewport, then
// we want to place the aircraft in the bottom left corner of the viewport relative
// to the hangar/ramp
export const calculateInitialPosition = (
  location: Location<Hangar | Ramp>,
  newAircraft: Aircraft,
  feetToPixels?: number,
  x0?: number,
  y0?: number
) => {
  if (!location || !newAircraft) {
    return;
  }

  if (feetToPixels) {
    // check to see if the entire location is within the viewport
    const y =
      y0 > 0
        ? y0
        : -1 * Math.max(newAircraft.length, newAircraft.wingspan) + y0;

    // for the x position, we don't want to place the aircraft on top of another aircraft
    const polygons = getStackPolygons(
      location.stack,
      1e8,
      feetToPixels,
      null,
      false
    );
    // increment the x position until we find a spot that doesn't intersect with any other aircraft
    let x = x0 || 0;
    while (
      polygons.some((polygon) =>
        doBoundsIntersect(polygon.bounds, [
          [x, y],
          [x + newAircraft.wingspan, y + newAircraft.length],
        ])
      )
    ) {
      x += 10;
    }

    return {
      x,
      y,
    };
  }

  return {
    x: null,
    y: -1 * Math.max(newAircraft.length, newAircraft.wingspan),
  };
};

/**
 * we need a wrapper b/c i need the useLocationsState container to be able to grab the
 * findTenantLocation function
 */
export const AddTenantButton: React.FC<Props> = (props) => (
  <LocationsContainer>
    <AddTenantButtonPresenter {...props} />
  </LocationsContainer>
);

type Props = {
  text?: string;
  location: Location<Hangar | Ramp>;
  sendToLocation: (
    location: Location<Ramp | Hangar>,
    stackName: string,
    tenant: Tenant
  ) => void;
  stack: StackType;
  setStack: (stack: StackType) => void;
  onAdd: (tenant: Tenant | Tenant[]) => void;
  hideAddMovableObstacle?: boolean;
  bulk?: boolean;
  disabled?: boolean;
  feetToPixels?: number;
  iconButton?: boolean;
  disableAutoOpen?: boolean;
  // Scheduled view props
  isScheduledView?: boolean;
  onAddTrip?: (trip: Trip) => void;
  currentTime?: Date;
};

export const AddTenantButtonPresenter: React.FC<Props> = ({
  location,
  sendToLocation,
  stack,
  setStack,
  onAdd,
  hideAddMovableObstacle = false,
  bulk = true,
  disabled = false,
  feetToPixels,
  iconButton = false,
  isScheduledView = false,
  onAddTrip,
  currentTime,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const { initialPositionOffset } = useLayoutCanvasState();
  const { predefinedObstacles } = usePredefinedObstacle();
  const snackbar = useSnackbar();

  const { airplxIdentity } = useIdentity();
  const { tenants, setTenants } = useTenants();
  const [localCurrentLocation, setLocalCurrentLocation] =
    React.useState<Location<Ramp | Hangar> | null>();
  const [localAssignedLocation, setLocalAssignedLocation] =
    React.useState<Location<Ramp | Hangar> | null>();
  const [open, setOpen] = React.useState<boolean>(false);
  const [selectedTab, setSelectedTab] = React.useState<string>("");
  const [warnings, setWarnings] = React.useState<string[]>([]);
  const [tailNumber, setTailNumber] = React.useState<string>("");
  const [owner, setOwner] = React.useState<string>("");
  const [note, setNote] = React.useState<string>("");
  const [type, setType] = React.useState<string>(
    stack?.isReference ? "base" : "transient"
  );
  const [bulkTenantsToAdd, setBulkTenantsToAdd] = React.useState<Tenant[]>([]);
  const [newAircraft, setNewAircraft] = React.useState<Aircraft>();
  const [showConfirmation, setShowConfirmation] = React.useState<string>("");
  const { activeFBO } = useActiveFBOs();

  // Add Trip tab ref and state
  const addTripTabRef = React.useRef<AddTripTabRef>(null);
  const [tripFormState, setTripFormState] = React.useState({
    isValid: false,
    loading: false,
  });

  // Bulk Upload tab ref and state
  const bulkUploadRef = React.useRef<SimpleBulkUploadRef>(null);
  const [bulkUploadFormState, setBulkUploadFormState] = React.useState({
    isValid: false,
    loading: false,
  });

  // Helper function to determine available tabs based on current props
  const getAvailableTabs = React.useCallback(() => {
    const tabs: string[] = [];

    if (!isScheduledView) {
      tabs.push("New Aircraft");
      if (Boolean(location)) {
        tabs.push("Prior Transients", "Tenants");
      }
      // Allow bulk add even without location (for Tenants page)
      if (bulk) {
        tabs.push("Bulk Add");
      }
    }

    if (isScheduledView && onAddTrip) {
      tabs.push("Add Trip", "All Trips", "Bulk Upload");
    }

    if (!hideAddMovableObstacle && Boolean(location)) {
      tabs.push("Add Movable Obstacle");
    }

    return tabs;
  }, [isScheduledView, location, bulk, onAddTrip, hideAddMovableObstacle]);

  /**
   * not sure where to put this...
   */
  const checkForWarnings = React.useCallback(
    (aircraft: Aircraft) => {
      const warnings = checkForAircraftWarnings(aircraft, location);
      if (!warnings) {
        return;
      }
      setWarnings(warnings);
    },
    [location]
  );

  // Reset form state when dialog opens (but not when dependencies change while dialog is already open)
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    // Only reset if dialog transitioned from closed to open
    if (open && !prevOpenRef.current) {
      setTailNumber("");
      setOwner("");
      setType(stack?.isReference ? "base" : "transient");
      setNote("");
      setWarnings([]);
      setNewAircraft(null);
      setLocalAssignedLocation(null);
      setLocalCurrentLocation(null);
      setBulkTenantsToAdd([]);
      setSelectedTab(""); // Reset tab selection when dialog opens
    }
    prevOpenRef.current = open;
  }, [open, stack]); // Track full stack object to ensure latest value when dialog opens

  // Set default tab when dialog opens (separate effect to avoid resetting form on dependency changes)
  React.useEffect(() => {
    if (open && !selectedTab) {
      const availableTabs = getAvailableTabs();
      // Use the first available tab as default
      if (availableTabs.length > 0) {
        // Prefer "Add Trip" for scheduled view, "New Aircraft" otherwise, or first available
        if (isScheduledView && availableTabs.includes("Add Trip")) {
          setSelectedTab("Add Trip");
        } else if (!isScheduledView && availableTabs.includes("New Aircraft")) {
          setSelectedTab("New Aircraft");
        } else {
          setSelectedTab(availableTabs[0]);
        }
      }
    }
  }, [open, selectedTab, isScheduledView, getAvailableTabs]);

  // Validate selectedTab when available tabs change
  React.useEffect(() => {
    if (open && selectedTab) {
      const availableTabs = getAvailableTabs();

      // If current tab is not available, reset to first available
      if (!availableTabs.includes(selectedTab) && availableTabs.length > 0) {
        // Prefer "Add Trip" for scheduled view, "New Aircraft" otherwise, or first available
        if (isScheduledView && availableTabs.includes("Add Trip")) {
          setSelectedTab("Add Trip");
        } else if (!isScheduledView && availableTabs.includes("New Aircraft")) {
          setSelectedTab("New Aircraft");
        } else {
          setSelectedTab(availableTabs[0]);
        }
      }
    }
  }, [open, selectedTab, getAvailableTabs, isScheduledView]);

  React.useEffect(() => {
    checkForWarnings(newAircraft);
  }, [newAircraft]);

  const onClickAddTenant = React.useCallback(
    async (formData: { tailNumber: string; owner: string; note: string }) => {
      if (!newAircraft) {
        return;
      }
      if (type === "base" && (location || selectedTab !== "New Aircraft")) {
        setShowConfirmation(
          addTenantConformationText(
            !location || location?.type === "hangar" ? "hangar" : "ramp"
          )
        );
        return;
      }
      const entity_id = uuidv4();
      const tenant: Tenant = {
        id: uuidv4(),
        placement_id: uuidv4(),
        fbo_id: activeFBO.id,
        entity_id,
        owner_manager: formData.owner,
        tail_number: formData.tailNumber,
        note: formData.note,
        note_last_updated: new Date().toISOString(),
        note_last_updated_by: airplxIdentity?.email ?? "Unknown",
        type: stack?.isReference ? "base" : type,
        aircraft_id: newAircraft.id,
        aircraft: newAircraft,
        selected: false,
        position: {
          id: consistentId(entity_id, stack?.isReference ?? false),
          stack_id: stack?.id,
          entity_id,
          ...calculateInitialPosition(
            location,
            newAircraft,
            feetToPixels,
            initialPositionOffset.xFeet,
            initialPositionOffset.yFeet
          ),
          angle: null,
          preferences: defaultPreferenceForAircraft(newAircraft, activeFBO),
        },
      };

      onAdd(tenant);
      setTenants(tenants.concat([tenant]));
      setNewAircraft(null);
      setTailNumber("");
      setOwner("");
      setNote("");
      // setOpen(false);
      snackbar.notify({
        text: location
          ? `${tenant.aircraft.model} added to ${location.name}`
          : `${tenant.aircraft.model} added`,
        severity: "success",
      });
      // i don't like this but there isn't a great alternative. we need to wait for the tenant
      // to be created, then put it into the correct hangars
      setTimeout(async () => {
        if (Boolean(localCurrentLocation)) {
          await sendToLocation(localCurrentLocation, "current", tenant);
        }
        if (Boolean(localAssignedLocation)) {
          await sendToLocation(localAssignedLocation, "reference", tenant);
        }
      }, 100);
      mixpanel.track("New Aircraft", {
        aircraft_tenant_type: type,
        aircraft_type: newAircraft.model,
        aircraft_tail_number: formData.tailNumber,
      });
    },
    [
      location,
      stack,
      type,
      newAircraft,
      onAdd,
      sendToLocation,
      tenants,
      feetToPixels,
      initialPositionOffset,
      activeFBO,
      airplxIdentity,
      localCurrentLocation,
      localAssignedLocation,
      snackbar,
    ]
  );

  const onClickBulkAdd = async () => {
    // bulk add
    setTenants([...tenants, ...bulkTenantsToAdd]);
    onAdd(bulkTenantsToAdd);
    // i don't like this but there isn't a great alternative. we need to wait for the tenant
    // to be created, then put it into the correct hangars
    setTimeout(async () => {
      if (Boolean(localCurrentLocation)) {
        for (const tenant of bulkTenantsToAdd) {
          await sendToLocation(localCurrentLocation, "current", tenant);
        }
      }
      if (Boolean(localAssignedLocation)) {
        for (const tenant of bulkTenantsToAdd) {
          await sendToLocation(localAssignedLocation, "reference", tenant);
        }
      }
    }, 100);
    setOpen(false);
  };

  const selectedTenant = React.useMemo(() => {
    return tenants
      ?.filter((t) => t.tail_number)
      .find(
        (t) =>
          t.tail_number.toLocaleUpperCase() === tailNumber.toLocaleUpperCase()
      );
  }, [tenants, tailNumber]);

  const onAddSelectedTenant = React.useCallback(
    async (
      tenant: Tenant,
      formData: { tailNumber: string; owner: string; note: string }
    ) => {
      if (!showConfirmation && location?.stack?.isReference) {
        setShowConfirmation(
          !location || location?.type === "hangar"
            ? "We'll assign this new Base Tenant to this hangar. If you need to change default hangar assignments, you can do that by editing this specific aircraft."
            : "We'll assign this new Tie-Down Tenant to this ramp. If you need to change default ramp assignments, you can do that by editing this specific aircraft."
        );
        return;
      }
      const entity_id = tenant.entity_id ?? uuidv4();
      const updatedTenant: Tenant = {
        ...tenant,
        entity_id,
        owner_manager: formData.owner || tenant.owner_manager,
        tail_number: formData.tailNumber || tenant.tail_number,
        note: formData.note,
        note_last_updated: formData.note
          ? new Date().toISOString()
          : tenant.note_last_updated,
        note_last_updated_by: formData.note
          ? airplxIdentity?.email ?? "Unknown"
          : tenant.note_last_updated_by,
        type: stack?.isReference ? "base" : tenant?.type ?? "transient",
        selected: false,
        position: {
          id: uuidv4(),
          entity_id: tenant.entity_id,
          stack_id: stack?.id,
          ...calculateInitialPosition(
            location,
            newAircraft,
            null,
            initialPositionOffset.xFeet,
            initialPositionOffset.yFeet
          ),
          angle: null,
          preferences: defaultPreferenceForAircraft(tenant.aircraft, activeFBO),
        },
      };

      if (sendToLocation && location && stack) {
        await sendToLocation(location, stack.name, updatedTenant);
      }
      onAdd({
        ...updatedTenant,
      });
      mixpanel.track("Add Aircraft", {
        aircraft_tenant_type: type,
        aircraft_type: newAircraft?.model,
        aircraft_tail_number: formData.tailNumber,
      });
      setNewAircraft(null);
      setTailNumber("");
      setOwner("");
      setType(stack?.isReference ? "base" : "transient");
      setNote("");
      setWarnings([]);
      snackbar.notify({
        text: location
          ? `${tenant.aircraft.model} added to ${location.name}`
          : `${tenant.aircraft.model} added`,
        severity: "success",
      });
      // setOpen(false);
    },
    [location, stack, onAdd, type, newAircraft, initialPositionOffset]
  );

  return (
    <>
      {iconButton ? (
        <Tooltip title={isScheduledView ? "Add Trip" : "Add Aircraft"}>
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={async () => setOpen(true)}
            sx={{
              alignSelf: "center",
              width: "80%",
            }}
          >
            <Stack direction="row" spacing={1}>
              <AirplanemodeActiveIcon />
              <AddIcon />
            </Stack>
          </Button>
        </Tooltip>
      ) : (
        <Button
          disabled={disabled}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={async () => setOpen(true)}
          color={"info"}
        >
          {isScheduledView ? "Add Trip" : "Add Aircraft"}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullScreen={isTablet}
        PaperProps={{
          sx: {
            ...(isTablet && {
              margin: 1,
              width: "calc(100% - 16px)",
              height: "calc(100% - 16px)",
            }),
          },
        }}
      >
        <DialogTitle>
          {isScheduledView ? "Add Trip" : "Add Aircraft"}
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            width: isTablet ? "100%" : 900,
            height: isTablet ? "auto" : 600,
            maxHeight: isTablet ? "calc(100vh - 180px)" : 600,
            overflowY: "auto",
            padding: isTablet ? 1.5 : 3,
            paddingBottom: isTablet ? 12 : 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack spacing={4} sx={{ height: "100%" }}>
            <Stack direction="column" spacing={2} sx={{ height: "100%" }}>
              {warnings.map((warning, idx) => (
                <Alert
                  key={`warning-${idx}`}
                  severity="error"
                  action={
                    <IconButton
                      size="small"
                      onClick={() =>
                        setWarnings(warnings.filter((w, i) => i !== idx))
                      }
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {warning}
                </Alert>
              ))}
              <Tabs
                value={selectedTab || false}
                onChange={(evt, newValue) => setSelectedTab(newValue)}
                aria-label="basic tabs example"
              >
                {!isScheduledView && (
                  <Tab value="New Aircraft" label="New Aircraft" />
                )}
                {!isScheduledView && Boolean(location) && (
                  <Tab value="Prior Transients" label="Prior Transients" />
                )}
                {!isScheduledView && Boolean(location) && (
                  <Tab value="Tenants" label="Tenants" />
                )}
                {!isScheduledView && bulk && (
                  <Tab value="Bulk Add" label="Bulk Add" />
                )}
                {isScheduledView &&
                  onAddTrip && [
                    <Tab key="add-trip" value="Add Trip" label="Add Trip" />,
                    <Tab key="all-trips" value="All Trips" label="All Trips" />,
                    <Tab
                      key="bulk-upload"
                      value="Bulk Upload"
                      label="Bulk Upload"
                    />,
                  ]}
                {!hideAddMovableObstacle && (
                  <Tab
                    value="Add Movable Obstacle"
                    label="Add Movable Obstacle"
                  />
                )}
              </Tabs>
              {selectedTab === "New Aircraft" && (
                <>
                  <NewAircraftForm
                    disableAutocomplete={false}
                    tailNumber={tailNumber}
                    setTailNumber={setTailNumber}
                    owner={owner}
                    setOwner={setOwner}
                    location={location}
                    setShowConfirmation={setShowConfirmation}
                    type={type}
                    setType={setType}
                    note={note}
                    setNote={setNote}
                    isReference={stack?.isReference}
                    newAircraft={newAircraft}
                    setNewAircraft={setNewAircraft}
                    assignedLocation={localAssignedLocation}
                    setAssignedLocation={setLocalAssignedLocation}
                    currentLocation={localCurrentLocation}
                    setCurrentLocation={setLocalCurrentLocation}
                  />
                  {Boolean(selectedTenant) && (
                    <AddSelectedTenant
                      selectedTenant={selectedTenant}
                      onAdd={(tenant) =>
                        onAddSelectedTenant(tenant, { tailNumber, owner, note })
                      }
                      isReference={stack?.isReference}
                      location={location}
                    />
                  )}
                </>
              )}
              {selectedTab === "Prior Transients" && (
                <TenantSelector
                  tenantType={TenantType.TRANSIENT}
                  filterBy={(t) => t.type === "transient"}
                  isReference={stack?.isReference}
                  checkAircraftHeight={checkForWarnings}
                  location={location}
                  stack={stack}
                  setStack={setStack}
                  feetToPixels={feetToPixels}
                />
              )}
              {selectedTab === "Tenants" && (
                <TenantSelector
                  tenantType={TenantType.BASE}
                  filterBy={(t) => t.type === "base"}
                  isReference={stack?.isReference}
                  checkAircraftHeight={checkForWarnings}
                  stack={stack}
                  setStack={setStack}
                  location={location}
                  feetToPixels={feetToPixels}
                />
              )}
              {selectedTab === "Bulk Add" && (
                <BulkAdd
                  stack={stack}
                  bulkTenantsToAdd={bulkTenantsToAdd}
                  setBulkTenantsToAdd={setBulkTenantsToAdd}
                />
              )}
              {selectedTab === "Add Trip" && isScheduledView && (
                <AddTripTab
                  ref={addTripTabRef}
                  location={location}
                  currentTime={currentTime}
                  tenants={tenants}
                  onAddTrip={onAddTrip}
                  onClose={() => setOpen(false)}
                  onStateChange={(isValid, loading) =>
                    setTripFormState({ isValid, loading })
                  }
                />
              )}
              {selectedTab === "All Trips" && isScheduledView && (
                <AllTripsTab
                  locationId={location?.id}
                  locationType={location?.type === "hangar" ? "hangar" : "ramp"}
                  currentTime={currentTime}
                  onAssignmentComplete={() => {
                    // Don't close dialog - let user see their changes
                    // Trigger any refresh needed
                    if (onAddTrip) {
                      // This will trigger a refresh of the scheduled view
                      onAddTrip(null);
                    }
                  }}
                  onCancel={() => setOpen(false)}
                />
              )}
              {selectedTab === "Bulk Upload" && isScheduledView && (
                <SimpleBulkUpload
                  ref={bulkUploadRef}
                  open={true}
                  onClose={() => {
                    // Don't close the parent dialog, just clear any state if needed
                  }}
                  onComplete={() => {
                    // Trigger refresh of scheduled view
                    if (onAddTrip) {
                      onAddTrip(null);
                    }
                  }}
                  autoAssignLocationId={location?.id}
                  autoAssignLocationType={
                    location?.type === "hangar" ? "hangar" : "ramp"
                  }
                  autoAssignLocationName={location?.name}
                  embedded={true}
                  onStateChange={(isValid, loading) =>
                    setBulkUploadFormState({ isValid, loading })
                  }
                />
              )}
              {selectedTab === "Add Movable Obstacle" && (
                <MovableObstacleGrid
                  location={location}
                  movableObstacles={predefinedObstacles}
                  stack={stack}
                  setStack={setStack}
                />
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            ...(isTablet && {
              position: "sticky",
              bottom: 0,
              backgroundColor: "background.paper",
              boxShadow: 4,
              borderTop: 1,
              borderColor: "divider",
              zIndex: 10,
            }),
          }}
        >
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ width: "100%" }}
          >
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              {selectedTab === "New Aircraft" || selectedTab === "Bulk Add" ? (
                <Button
                  disabled={
                    (selectedTab === "New Aircraft" &&
                      (Boolean(selectedTenant) || !Boolean(newAircraft))) ||
                    (selectedTab === "Bulk Add" &&
                      bulkTenantsToAdd.length === 0)
                  }
                  variant="contained"
                  color="success"
                  onClick={() => {
                    if (selectedTab === "New Aircraft") {
                      onClickAddTenant({ tailNumber, owner, note });
                    } else {
                      // bulk add
                      onClickBulkAdd();
                    }
                  }}
                >
                  Add
                </Button>
              ) : selectedTab === "Add Trip" ? (
                <Button
                  disabled={!tripFormState.isValid || tripFormState.loading}
                  variant="contained"
                  color="success"
                  onClick={() => addTripTabRef.current?.createTrip()}
                >
                  {tripFormState.loading ? "Creating..." : "Create Trip"}
                </Button>
              ) : selectedTab === "Bulk Upload" ? (
                <Button
                  disabled={
                    !bulkUploadFormState.isValid || bulkUploadFormState.loading
                  }
                  variant="contained"
                  color="success"
                  onClick={() => bulkUploadRef.current?.save()}
                >
                  {bulkUploadFormState.loading ? "Saving..." : "Save Trips"}
                </Button>
              ) : selectedTab === "All Trips" ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => setOpen(false)}
                >
                  Done
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => setOpen(false)}
                >
                  Done
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>
      {showConfirmation && (
        <ConfirmationDialog
          title="Please Confirm"
          text={showConfirmation}
          onCancel={() => {
            // do nothing
          }}
          onClose={() => {
            setShowConfirmation("");
          }}
          onOk={() => {
            if (!newAircraft) {
              setShowConfirmation("");
              setType("base");
              return;
            }
            const aircraft = selectedTenant?.aircraft ?? newAircraft;
            const entity_id = selectedTenant?.entity_id ?? uuidv4();
            const tenant: Tenant = {
              id: selectedTenant?.id ?? uuidv4(),
              fbo_id: activeFBO?.id,
              placement_id: uuidv4(),
              entity_id: selectedTenant?.entity_id ?? entity_id,
              owner_manager: owner || selectedTenant?.owner_manager,
              tail_number: selectedTenant?.tail_number ?? tailNumber,
              type: stack?.isReference ? "base" : type,
              aircraft_id: aircraft.id,
              aircraft: aircraft,
              selected: false,
              position: {
                id: consistentId(entity_id, stack?.isReference ?? true),
                stack_id: stack?.id,
                entity_id,
                ...calculateInitialPosition(
                  location,
                  newAircraft,
                  null,
                  initialPositionOffset.xFeet,
                  initialPositionOffset.yFeet
                ),
                angle: null,
                preferences: defaultPreferenceForAircraft(aircraft, activeFBO),
              },
            };

            onAdd(tenant);
            setNewAircraft(null);
            setTailNumber("");
            setOwner("");
            setNote("");
            setShowConfirmation("");
            setOpen(false);
            // i don't like this but there isn't a great alternative. we need to wait for the tenant
            // to be created, then put it into the correct hangars
            setTimeout(async () => {
              if (Boolean(localCurrentLocation)) {
                await sendToLocation(localCurrentLocation, "current", tenant);
              }
              if (Boolean(localAssignedLocation)) {
                await sendToLocation(
                  localAssignedLocation,
                  "reference",
                  tenant
                );
              }
            }, 100);
          }}
        />
      )}
    </>
  );
};
